import React, { useEffect, useRef, useState } from "react";
import {
  CameraFill,
  ChevronLeft,
  PlusLg,
  Shop,
  Trash,
} from "react-bootstrap-icons";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { useNavigate, useParams } from "react-router-dom";
import { decryptData, encryptData } from "../../utils/crypto";
import Utils from "../../utils";
import { useTranslation } from "react-i18next";
import {
  getCityList,
  getCountryList,
  getStateList,
} from "../../services/commonServices";
import Select from "react-select";
import { customStyles } from "../../utils/function";
import {
  customerProfileUpdateProfile,
  getCustomerProfileDetails,
} from "../../services/authServices";
import { notify } from "../../components/Common/Alerts";
import CONFIG from "../../config";

const CustomerProfile = () => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const { t } = useTranslation();
  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark",
  );
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    address: "",
    landmark: "",
    state_id: "",
    country_id: "",
    pincode: "",
    latitude: "",
    longitude: "",
    email: "",
    confirm_password: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    mobile: "",
    name: "",
    address: "",
    landmark: "",
    state_id: "",
    country_id: "",
    pincode: "",
    latitude: "",
    longitude: "",
    email: "",
    confirm_password: "",
    password: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [storeOwnerDetails, setStoreOwnerDetails] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [cityList, setCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [map, setMap] = useState(null);
  const [marker, setMarker] = useState(null);

  useEffect(() => {
    const handleStorageChange = () => {
      setDarkMode(localStorage.getItem("theme") === "dark");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];

      setFile(selectedFile);
      setPreview(URL.createObjectURL(selectedFile));
    }
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setFile(null);
    setPreview(null);
  };

  const validateField = (name, value) => {
    switch (name) {
      case "name":
        if (!value) return t("nameIsRequired");
        return "";

      case "mobile":
        if (!value) return t("mobileIsRequired");
        const mobileRegex = /^\d{10}$/;
        if (!mobileRegex.test(value)) {
          return t("enterDigitMobileNumber");
        }
        return "";

      case "address":
        if (!value) return t("addressIsRequired");
        return "";

      case "landmark":
        if (!value) return t("landmarkIsRequired");
        return "";

      case "city_id":
        if (!value) return t("cityIsRequired");
        return "";

      case "country_id":
        if (!value) return t("countryIsRequired");
        return "";

      case "state_id":
        if (!value) return t("stateIsRequired");
        return "";

      case "pincode":
        if (!value) return t("pincodeIsRequired");
        return "";

      // case "password":
      //   if (value.length < 6) return t("passwordCharactersLong");
      //   if (!/[a-zA-Z]/.test(value)) return t("passwordMustLetter");
      //   return "";

      // case "confirm_password":
      //   if (formData.password) {
      //     if (!value) return t("confirmPasswordIsRequired");
      //     if (value !== formData.password) return t("passwordsDoNotMatch");
      //   }
      //   return "";

      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value && !emailRegex.test(value)) {
          return t("pleaseEnterValidEmail");
        }
        return "";

      default:
        return "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value, { ...formData, [name]: value }),
    }));
  };

  useEffect(() => {
    if (!window.google) return;

    const hasSavedLocation = formData?.latitude && formData?.longitude;

    const initMap = (lat, lng) => {
      const mapInstance = new window.google.maps.Map(
        document.getElementById("map"),
        {
          center: { lat, lng },
          zoom: 15,
        },
      );

      const markerInstance = new window.google.maps.Marker({
        position: { lat, lng },
        map: mapInstance,
        draggable: true,
      });

      setMap(mapInstance);
      setMarker(markerInstance);

      markerInstance.addListener("dragend", () => {
        const pos = markerInstance.getPosition();

        const newLat = pos.lat();
        const newLng = pos.lng();

        setFormData((prev) => ({
          ...prev,
          latitude: newLat,
          longitude: newLng,
        }));

        getAddressFromLatLng(newLat, newLng);
      });
    };

    if (hasSavedLocation) {
      initMap(Number(formData.latitude), Number(formData.longitude));
      return;
    }

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;

          setFormData((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
          }));

          initMap(lat, lng);
        },
        (error) => {
          console.log("GPS error:", error);

          initMap(23.0225, 72.5714);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );
    } else {
      initMap(23.0225, 72.5714);
    }
  }, [formData?.latitude, formData?.longitude]);

  const getAddressFromLatLng = (lat, lng) => {
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status !== "OK" || !results[0]) return;

      const result = results[0];

      let address = "";
      let cityName = "";
      let stateName = "";
      let countryName = "";
      let pincode = "";
      let landmark = "";

      result.address_components.forEach((component) => {
        const types = component.types;

        if (types.includes("postal_code")) {
          pincode = component.long_name;
        }

        if (
          types.includes("locality") ||
          types.includes("administrative_area_level_2")
        ) {
          cityName = component.long_name;
        }

        if (types.includes("administrative_area_level_1")) {
          stateName = component.long_name;
        }

        if (types.includes("country")) {
          countryName = component.long_name;
        }

        if (
          types.includes("sublocality_level_1") ||
          types.includes("sublocality_level_2") ||
          types.includes("neighborhood") ||
          types.includes("premise")
        ) {
          landmark = component.long_name;
        }
      });

      address = result.formatted_address.split(",").slice(0, 2).join(", ");

      const countryObj = countryList.find((c) => c.label === countryName);

      const stateObj = stateList.find((s) => s.label === stateName);

      const cityObj = cityList.find((c) => c.label === cityName);

      setFormData((prev) => ({
        ...prev,
        address: address,
        landmark: landmark || cityName,
        pincode: pincode,

        country_id: countryObj?.value ? String(countryObj.value) : "",
        state_id: stateObj?.value ? String(stateObj.value) : "",
        city_id: cityObj?.value ? String(cityObj.value) : "",

        latitude: lat,
        longitude: lng,
      }));
    });
  };

  const getLatLngFromAddress = (address) => {
    if (!window.google) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status !== "OK" || !results[0]) return;

      const location = results[0].geometry.location;

      const lat = location.lat();
      const lng = location.lng();

      setFormData((prev) => ({
        ...prev,
        latitude: lat,
        longitude: lng,
      }));

      if (map) map.setCenter({ lat, lng });
      if (marker) marker.setPosition({ lat, lng });
    });
  };

  useEffect(function () {
    getCountryList()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        const formatted = decrypted.data.map((item) => ({
          value: item.id,
          label: item.name,
        }));
        setCountryList(formatted);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(() => {
    if (formData?.country_id) {
      let data = {
        country_id: formData.country_id,
      };

      getStateList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);

          const formatted = decrypted.data.map((item) => ({
            value: item.id,
            label: item.name,
          }));

          setStateList(formatted);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [formData?.country_id]);

  useEffect(() => {
    if (formData?.state_id) {
      let data = {
        state_id: formData.state_id,
      };

      getCityList(data)
        .then((response) => {
          const decrypted = decryptData(response.data.data);

          const formatted = decrypted.data.map((item) => ({
            value: item.id,
            label: item.name,
          }));

          setCityList(formatted);
        })
        .catch((err) => {
          notify.error(Utils.getErrorMessage(err));
        });
    }
  }, [formData?.state_id]);

  useEffect(function () {
    getCustomerProfileDetails()
      .then((response) => {
        const decrypted = decryptData(response.data.data);
        setStoreOwnerDetails(decrypted.data);
      })
      .catch((err) => {
        notify.error(Utils.getErrorMessage(err));
      });
  }, []);

  useEffect(
    function () {
      if (storeOwnerDetails) {
        setFormData({
          name: storeOwnerDetails?.name,
          mobile: storeOwnerDetails?.mobile,
          address: storeOwnerDetails?.address,
          landmark: storeOwnerDetails?.landmark,
          city_id: storeOwnerDetails?.city_id,
          state_id: storeOwnerDetails?.state_id,
          country_id: storeOwnerDetails?.country_id,
          pincode: storeOwnerDetails?.pincode,
          latitude: storeOwnerDetails?.latitude,
          longitude: storeOwnerDetails?.longitude,
          email: storeOwnerDetails?.email,
        });
        setFile(storeOwnerDetails?.profile_image);
        setPreview(storeOwnerDetails?.profile_image);
      }
    },
    [storeOwnerDetails],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      name: validateField("name", formData.name),
      mobile: validateField("mobile", formData.mobile),
      address: validateField("address", formData.address),
      city_id: validateField("city_id", formData.city_id),
      country_id: validateField("country_id", formData.country_id),
      state_id: validateField("state_id", formData.state_id),
      pincode: validateField("pincode", formData.pincode),
      landmark: validateField("landmark", formData?.landmark),
      // confirm_password: formData.password
      //   ? validateField("confirm_password", formData.confirm_password)
      //   : "",
    };
    Utils.focusError();
    setErrors(newErrors);
    const hasErrors = Object.values(newErrors).some((err) => err);

    if (hasErrors) return;

    setBtnLoading(true);

    const payload = {
      name: formData.name?.trim() || "",
      mobile: formData.mobile || "",
      address: formData.address?.trim() || "",
      landmark: formData.landmark?.trim() || "",
      state_id: formData.state_id || "",
      country_id: formData.country_id || "",
      city_id: formData.city_id || "",
      pincode: formData.pincode || "",
      latitude: formData.latitude || "0.00",
      longitude: formData.longitude || "0.00",
      password: formData.password || "",
      email: formData.email || "",
      is_delete_icon: file ? 0 : 1,
    };
    let q = encryptData(payload);

    const formPayload = new FormData();

    if (file) {
      formPayload.append("files", file);
    }
    formPayload.append("q", q);

    try {
      const response = await customerProfileUpdateProfile(formPayload);
      const decrypted = decryptData(response.data.data);
      notify.success(decrypted.message);
      const storedData = JSON.parse(localStorage.getItem(CONFIG.login_data));

      const updatedData = {
        ...storedData,
        user: {
          ...storedData.user,
          full_name: decrypted?.data?.full_name,
          mobile: decrypted?.data?.mobile,
          email: decrypted?.data?.email,
        },
      };
      localStorage.setItem(CONFIG.login_data, JSON.stringify(updatedData));
      setErrors("");
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const getSelectedOption = (list, value) =>
    list.find((opt) => String(opt.value) === String(value)) || null;

  return (
    <div>
      <div className="mx-auto space-y-8 animate-fade-up pb-12">
        <div className="flex items-center gap-4 md:gap-6 flex-shrink-0 px-2 mb-2 hidden md:flex">
          <button
            onClick={() => navigate("/dashboard")}
            className="group flex items-center justify-center w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2.5 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-all active:scale-95 shadow-sm"
          >
            <ChevronLeft
              size={18}
              className="group-hover:-translate-x-1 transition-transform"
            />
            <span className="hidden ml-2 font-bold text-sm">{t("back")}</span>
          </button>

          <div className="flex-1">
            <h2 className="text-xl md:text-3xl font-black text-slate-800 dark:text-white tracking-tight leading-tight">
              {t("updateProfile")}{" "}
            </h2>
          </div>
        </div>

        <section className="bg-white dark:bg-slate-900 rounded-[32px] border border-slate-100 dark:border-white/5 p-6 lg:p-10 shadow-sm transition-all">
          {/* Header Section */}
          <div className="flex items-center justify-between mb-10 pb-6 border-b border-slate-50 dark:border-white/5">
            <div className="flex items-center gap-4">
              <div></div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-3 flex flex-col items-center justify-start pt-2">
              <div
                className="relative group cursor-pointer"
                onClick={() => inputRef.current.click()}
              >
                <input
                  type="file"
                  ref={inputRef}
                  onChange={handleFileChange}
                  accept="image/*"
                  className="hidden"
                />

                <div className="w-36 h-36 bg-gray-50 rounded-full border-4 border-white shadow-xl flex items-center justify-center overflow-hidden transition-transform group-hover:scale-105">
                  {preview ? (
                    <img
                      src={preview}
                      alt="preview"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <CameraFill size={32} className="text-gray-200" />
                  )}
                </div>

                {!file && (
                  <div className="absolute bottom-2 right-2 bg-brand-orange text-white p-2.5 rounded-full border-4 border-white shadow-lg">
                    <PlusLg size={16} />
                  </div>
                )}

                {file && (
                  <div
                    onClick={handleDelete}
                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full border-4 border-white shadow-lg"
                  >
                    <Trash size={14} />
                  </div>
                )}
              </div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-6">
                {t("profilePicture")}
              </p>
            </div>

            <div className="lg:col-span-9">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="name" className="input-label">
                    {t("name")}{" "}
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder={t("enterCustomerName")}
                    className={`input-field ${errors.name ? "input-error" : ""}`}
                    value={formData?.name || ""}
                    onChange={handleChange}
                    maxLength={50}
                  />
                  {errors.name && (
                    <span className="text-danger">{errors.name}</span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="mobile" className="input-label">
                    {t("mobileNumber")}{" "}
                  </label>
                  <input
                    type="number"
                    name="mobile"
                    placeholder={t("enterMobileNumber")}
                    className={`input-field 
                    ${errors.mobile ? "input-error border-red-500" : ""}
                    disabled:bg-white disabled:text-black disabled:opacity-100 disabled:cursor-not-allowed dark:disabled:bg-black dark:disabled:text-white`}
                    value={formData?.mobile || ""}
                    onChange={handleChange}
                    min={0}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    disabled
                  />
                  {errors.mobile && (
                    <span className="text-xs text-red-500 mt-1">
                      {errors.mobile}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="input-label">
                    {t("email")}
                  </label>
                  <input
                    type="text"
                    name="email"
                    placeholder={t("enterYourEmail")}
                    className={`input-field ${errors.email ? "input-error" : ""}`}
                    value={formData?.email || ""}
                    onChange={handleChange}
                  />
                  {errors.email && (
                    <span className="text-danger">{errors.email}</span>
                  )}
                </div>

                <div className="flex flex-col gap-2 md:col-span-2">
                  <label htmlFor="address" className="input-label">
                    {t("address")}{" "}
                  </label>
                  <textarea
                    name="address"
                    placeholder={t("enterYourAddress")}
                    className={`input-field ${errors.address ? "input-error" : ""}`}
                    value={formData?.address || ""}
                    onChange={handleChange}
                    onBlur={(e) => getLatLngFromAddress(e.target.value)}
                    rows={3}
                  />
                  {errors.address && (
                    <span className="text-danger">{errors.address}</span>
                  )}
                </div>

                <div className="flex flex-col gap-2">
                  <label htmlFor="landmark" className="input-label">
                    {t("landmark")}
                  </label>
                  <input
                    type="text"
                    name="landmark"
                    placeholder={t("enterYourLandmark")}
                    className={`input-field ${errors.address ? "input-error" : ""}`}
                    value={formData?.landmark || ""}
                    onChange={handleChange}
                    maxLength={200}
                  />
                  {errors.landmark && (
                    <span className="text-danger">{errors.landmark}</span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 md:col-span-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="country_id" className="input-label">
                      {t("country")}
                    </label>

                    <Select
                      options={countryList}
                      value={getSelectedOption(
                        countryList,
                        formData.country_id,
                      )}
                      onChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          country_id: String(selected?.value ?? ""),
                          state_id: "",
                          city_id: "",
                        }));
                        setStateList([]);
                        setCityList([]);
                      }}
                      styles={customStyles(darkMode)}
                      placeholder={t("selectCountry")}
                      isClearable={true}
                      isSearchable={true}
                    />
                    {errors.country_id && (
                      <span className="text-danger">{errors.country_id}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="state_id" className="input-label">
                      {t("state")}
                    </label>
                    <Select
                      options={stateList}
                      value={getSelectedOption(stateList, formData.state_id)}
                      onChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          state_id: String(selected?.value ?? ""),
                          city_id: "",
                        }));
                        setCityList([]);
                      }}
                      styles={customStyles(darkMode)}
                      placeholder={t("selectState")}
                      isClearable={true}
                      isSearchable={true}
                    />
                    {errors.state_id && (
                      <span className="text-danger">{errors.state_id}</span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <label htmlFor="city_id" className="input-label">
                      {t("city")}
                    </label>
                    <Select
                      options={cityList}
                      value={getSelectedOption(cityList, formData.city_id)}
                      onChange={(selected) =>
                        setFormData((prev) => ({
                          ...prev,
                          city_id: String(selected?.value ?? ""),
                        }))
                      }
                      styles={customStyles(darkMode)}
                      placeholder={t("selectCity")}
                      isClearable={true}
                      isSearchable={true}
                    />
                    {errors.city_id && (
                      <span className="text-danger">{errors.city_id}</span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 md:col-span-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label htmlFor="pincode" className="input-label">
                      {t("pincode")}
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      placeholder={t("enteryourPincode")}
                      className={`input-field ${errors.pincode ? "input-error" : ""}`}
                      value={formData?.pincode || ""}
                      onChange={(e) => {
                        const value = e.target.value
                          .replace(/\D/g, "")
                          .slice(0, 6);

                        setFormData((prev) => ({
                          ...prev,
                          pincode: value,
                        }));

                        setErrors((prev) => ({
                          ...prev,
                          pincode: validateField("pincode", value, {
                            ...formData,
                            pincode: value,
                          }),
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (["e", "E", "+", "-", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                    {errors.pincode && (
                      <span className="text-danger">{errors.pincode}</span>
                    )}
                  </div>

                  {/* <div className="flex flex-col gap-2">
                    <label
                      htmlFor="password"
                      name="password"
                      className="input-label"
                    >
                      {t("password")}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="••••••••"
                        className={`input-field w-full pr-10 ${errors.password ? "input-error" : ""}`}
                        value={formData?.password || ""}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showPassword ? (
                          <Eye size={18} />
                        ) : (
                          <EyeSlash size={18} />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <span className="text-danger">{errors.password}</span>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <label
                      htmlFor="confirmPassword"
                      name="confirmPassword"
                      className="input-label"
                    >
                      {t("confirmPassword")}{" "}
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="••••••••"
                        className={`input-field w-full pr-10 ${errors.confirm_password ? "input-error" : ""}`}
                        name="confirm_password"
                        value={formData?.confirm_password || ""}
                        onChange={handleChange}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showConfirmPassword ? (
                          <Eye size={18} />
                        ) : (
                          <EyeSlash size={18} />
                        )}
                      </button>
                    </div>
                    {errors.confirm_password && (
                      <span className="text-danger">
                        {errors.confirm_password}
                      </span>
                    )}
                  </div> */}

                  <div className="h-[300px] rounded-[24px] overflow-hidden border-2 border-dashed border-slate-200">
                    <div id="map" className="w-full h-full" />
                  </div>
                </div>

                <div className="md:col-span-2 flex items-center justify-end gap-3 pt-6 border-t border-slate-50 dark:border-white/5 mt-4">
                  <button
                    onClick={handleSubmit}
                    disabled={btnLoading}
                    className="px-8 py-2.5 text-sm font-bold text-white bg-orange-500 hover:bg-orange-600 rounded-xl shadow-lg shadow-orange-500/30 transition-all flex items-center gap-2"
                  >
                    {btnLoading && (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    )}
                    {btnLoading ? t("submitting") : t("update")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default CustomerProfile;
